import React from "react";
import styled from "styled-components";
import { device } from "../../lib/breakpoints";
import {
  DollarOutlined,
  SendOutlined,
  StarFilled,
} from "@ant-design/icons";
const BLACK = "#000000";
const SLATE_800 = "#1e293b";
const SLATE_300 = "#cbd5e1";
const SLATE_MUTED = "#64748b";
const ORANGE = "#FF5722";
const GREEN = "#00C853";
const WHITE = "#ffffff";
const CARD_BG = "rgba(2, 6, 23, 0.92)";

const MOCK_BIDS = [
  {
    vendor: "Northside Plumbing Co.",
    bid: "$2,180",
    eta: "3-5 days",
    rating: 4.8,
    reviews: 214,
    note: "Preferred vendor",
  },
  {
    vendor: "Metro Rooter LLC",
    bid: "$2,340",
    eta: "1 week",
    rating: 4.6,
    reviews: 89,
    note: null,
  },
  {
    vendor: "Ace Drain & Sewer",
    bid: "$2,520",
    eta: "5 days",
    rating: 4.4,
    reviews: 156,
    note: null,
  },
];

const VENDOR_STEPS = [
  {
    Icon: DollarOutlined,
    role: "Clumse to you",
    tone: "owner",
    body: "Search fee paid. We're reaching out to vetted pros in your ZIP.",
  },
  {
    Icon: SendOutlined,
    role: "Clumse to vendor",
    tone: "system",
    body: "Kitchen leak at ZIP 63109. Reply with bid and timeline.",
  },
  {
    Icon: DollarOutlined,
    role: "Vendor",
    tone: "vendor",
    body: "$2,340 bid. Can start Thursday. Licensed and insured.",
  },
];

function StarRow({ rating }) {
  const full = Math.min(5, Math.floor(rating));
  const partial = Math.min(1, Math.max(0, rating - full));
  return (
    <Stars aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => {
        let opacity = 0.35;
        let color = "rgba(148, 163, 184, 0.35)";
        if (i <= full) {
          opacity = 1;
          color = GREEN;
        } else if (i === full + 1 && partial > 0) {
          opacity = 0.35 + partial * 0.65;
          color = GREEN;
        }
        return (
          <StarFilled
            key={i}
            style={{
              color,
              opacity,
              fontSize: "0.65rem",
            }}
          />
        );
      })}
      <RatingNum>{rating.toFixed(1)}</RatingNum>
    </Stars>
  );
}

/**
 * Expands the hero “quotes to savings” demo: operator-facing bid table + anonymized vendor relay.
 * Placed above the pricing economics section on `/`.
 */
export default function HomeBidInboxDemoSection() {
  return (
    <Outer id="bid-inbox-demo" aria-labelledby="bid-inbox-demo-heading">
      <MainShell>
        <SectionHeading id="bid-inbox-demo-heading">
          When bids land, you see them in one place
        </SectionHeading>
        <Lead>
          Pay the search fee, we contact vetted local pros, and when they
          respond you see bids in one place with names, pricing, ratings, and
          contact info. Compare options, then reach out to your preferred pro
          directly.
        </Lead>

        <TwoCol>
          <Panel>
            <PanelTitle>Your bid summary</PanelTitle>
            <PanelSub>
              One ranked table with full transparency. Review contractor names,
              ratings from verified work, and contact them directly.
            </PanelSub>

            <TableWrap>
              <Table role="presentation">
                <thead>
                  <tr>
                    <Th>Vendor</Th>
                    <Th>Bid</Th>
                    <Th>ETA</Th>
                    <Th>Reviews</Th>
                  </tr>
                </thead>
                <tbody>
                  {MOCK_BIDS.map((row) => (
                    <tr key={row.vendor}>
                      <Td>
                        <VendorCell>
                          <VendorName>{row.vendor}</VendorName>
                          {row.note ? (
                            <BadgeWrap>
                              <Badge>{row.note}</Badge>
                            </BadgeWrap>
                          ) : null}
                        </VendorCell>
                      </Td>
                      <Td $mono>{row.bid}</Td>
                      <Td>{row.eta}</Td>
                      <Td>
                        <ReviewCell>
                          <StarRow rating={row.rating} />
                          <ReviewCount>({row.reviews})</ReviewCount>
                        </ReviewCell>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
            <FootNote>
              Outreach starts after you pay the search fee. When pros respond,
              you get full contractor details including names, ratings, and
              contact info. Coordinate scheduling and payment directly with
              your chosen pro.
            </FootNote>
          </Panel>

          <Panel>
            <PanelTitle>How vendors interact</PanelTitle>
            <PanelSub>
              Pay the search fee first. We reach out to contractors on your
              scope and ZIP. When they quote, you get their bids, names,
              ratings, and contact info to coordinate directly.
            </PanelSub>

            <ChatStack>
              {VENDOR_STEPS.map((m, i) => {
                const StepIcon = m.Icon;
                return (
                  <Bubble key={i} $tone={m.tone}>
                    <StepIconWrap $tone={m.tone} aria-hidden="true">
                      <StepIcon />
                    </StepIconWrap>
                    <BubbleBody>
                      <BubbleMeta>{m.role}</BubbleMeta>
                      <BubbleText>{m.body}</BubbleText>
                    </BubbleBody>
                  </Bubble>
                );
              })}
            </ChatStack>
            <RelayNote>
              <RelayKey>Direct Contact</RelayKey> model means you coordinate
              directly with your chosen contractor. Pay contractors directly
              after work completion.
            </RelayNote>
          </Panel>
        </TwoCol>
      </MainShell>
    </Outer>
  );
}

const Outer = styled.section`
  position: relative;
  z-index: 1;
  width: 100%;
  background: ${BLACK};
  border-top: 1px solid ${SLATE_800};
  padding: clamp(3.5rem, 6.5vw, 5.25rem) 0 clamp(3.5rem, 7vw, 5.5rem);
  box-sizing: border-box;
`;

const MainShell = styled.div`
  width: 100%;
  max-width: 1536px;
  margin: 0 auto;
  padding-left: clamp(1rem, 4vw, 4rem);
  padding-right: clamp(1rem, 4vw, 4rem);
  box-sizing: border-box;
`;

const SectionHeading = styled.h2`
  margin: 0;
  font-family: "Inter Tight", "Inter", system-ui, sans-serif;
  font-size: clamp(1.35rem, 3vw, 1.85rem);
  font-weight: 900;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: ${WHITE};
  text-align: center;
`;

const Lead = styled.p`
  margin: 0.85rem auto 0;
  max-width: 44rem;
  font-family: "Inter", system-ui, sans-serif;
  font-size: clamp(0.9rem, 1.6vw, 1.02rem);
  line-height: 1.55;
  color: rgba(248, 250, 252, 0.78);
  text-align: center;
`;

const TwoCol = styled.div`
  margin-top: clamp(1.5rem, 3vw, 2rem);
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
  align-items: stretch;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;

  @media (min-width: 960px) {
    grid-template-columns: 1.15fr 1fr;
    gap: 1.5rem;
  }
`;

const Panel = styled.div`
  background: ${CARD_BG};
  border: 1px solid ${SLATE_800};
  padding: clamp(1.1rem, 2vw, 1.35rem);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  max-width: 100%;
`;

const PanelTitle = styled.h3`
  margin: 0;
  font-family: "Inter Tight", "Inter", system-ui, sans-serif;
  font-size: clamp(1rem, 1.8vw, 1.15rem);
  font-weight: 800;
  color: ${WHITE};
  letter-spacing: -0.01em;
`;

const PanelSub = styled.p`
  margin: 0.4rem 0 0;
  font-family: "Inter", system-ui, sans-serif;
  font-size: clamp(0.78rem, 1.2vw, 0.88rem);
  line-height: 1.45;
  color: ${SLATE_MUTED};
`;

const TableWrap = styled.div`
  margin-top: 0.95rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  text-align: left;

  @media ${device.mobile} {
    touch-action: pan-x;
  }
`;

const Table = styled.table`
  width: 100%;
  max-width: 100%;
  border-collapse: collapse;
  font-family: var(--font-stack-mono);
  font-size: clamp(0.62rem, 1vw, 0.72rem);
  text-align: left;
  box-sizing: border-box;

  @media ${device.mobile} {
    /* Wider than the card when needed; parent scrolls horizontally. */
    width: max-content;
    max-width: none;
  }
`;

const Th = styled.th`
  text-align: left;
  padding: 0.55rem 0.55rem 0.65rem;
  border-bottom: 1px solid ${SLATE_800};
  color: rgba(248, 250, 252, 0.55);
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;

  @media ${device.mobile} {
    padding: 0.55rem 0.65rem 0.65rem;
  }
`;

const Td = styled.td`
  padding: clamp(0.85rem, 2.5vw, 1.25rem) 0.55rem;
  border-bottom: 1px solid rgba(30, 41, 59, 0.85);
  color: rgba(248, 250, 252, 0.9);
  vertical-align: middle;
  text-align: left;
  font-weight: ${(p) => (p.$mono ? 800 : 600)};
  letter-spacing: ${(p) => (p.$mono ? "0.04em" : "0.02em")};

  @media ${device.mobile} {
    padding: 0.65rem 0.65rem;
    /* Keep money, ETA, and reviews on one line inside the scrollable strip. */
    &:nth-of-type(2),
    &:nth-of-type(3),
    &:nth-of-type(4) {
      white-space: nowrap;
    }
  }
`;

const VendorCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  min-width: 0;

  @media ${device.mobile} {
    min-width: 11.5rem;
  }

  @media ${device.minTablet} {
    min-width: 8.5rem;
  }
`;

const VendorName = styled.span`
  display: block;
  text-align: left;
  overflow-wrap: break-word;
  word-break: normal;
`;

const BadgeWrap = styled.div`
  width: 100%;
  max-width: 100%;
  display: flex;
  justify-content: flex-start;
`;

const Badge = styled.span`
  display: inline-block;
  max-width: 100%;
  padding: 0.12rem 0.45rem;
  border: 1px solid ${ORANGE};
  color: ${ORANGE};
  font-size: 0.55rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  overflow-wrap: break-word;
`;

const ReviewCell = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: 0.35rem;
  min-width: 0;
`;

const Stars = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.08rem;
`;

const RatingNum = styled.span`
  margin-left: 0.2rem;
  color: ${GREEN};
  font-weight: 800;
  font-size: 0.68rem;
`;

const ReviewCount = styled.span`
  color: ${SLATE_300};
  font-weight: 600;
  font-size: 0.62rem;
`;

const FootNote = styled.p`
  margin: 0.85rem 0 0;
  font-family: "Inter", system-ui, sans-serif;
  font-size: clamp(0.72rem, 1.1vw, 0.8rem);
  line-height: 1.45;
  color: rgba(248, 250, 252, 0.55);
  overflow-wrap: break-word;
  word-break: break-word;
`;

const ChatStack = styled.div`
  margin-top: 0.95rem;
  flex: 1;
  display: grid;
  grid-template-rows: repeat(3, 1fr);
  gap: 0.65rem;
  min-height: 0;
`;

const Bubble = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  text-align: left;
  padding: 0.75rem 0.85rem;
  box-sizing: border-box;
  border-left: 3px solid
    ${(p) =>
      p.$tone === "vendor"
        ? ORANGE
        : p.$tone === "owner"
          ? GREEN
          : "rgba(148, 163, 184, 0.5)"};
  background: rgba(255, 255, 255, 0.03);
  border-radius: 0;
`;

const STEP_ICON_SIZE = "2.5rem";

const StepIconWrap = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-sizing: border-box;
  width: ${STEP_ICON_SIZE};
  height: ${STEP_ICON_SIZE};
  border: 1px solid
    ${(p) =>
      p.$tone === "vendor"
        ? ORANGE
        : p.$tone === "owner"
          ? GREEN
          : "rgba(148, 163, 184, 0.45)"};
  color: ${(p) =>
    p.$tone === "vendor" ? ORANGE : p.$tone === "owner" ? GREEN : SLATE_300};
  background: rgba(255, 255, 255, 0.04);

  .anticon {
    font-size: 1rem;
  }
`;

const BubbleBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const BubbleMeta = styled.div`
  font-family: var(--font-stack-mono);
  font-size: 0.52rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(248, 250, 252, 0.45);
  margin-bottom: 0.3rem;
`;

const BubbleText = styled.p`
  margin: 0;
  font-family: "Inter", system-ui, sans-serif;
  font-size: clamp(0.78rem, 1.15vw, 0.88rem);
  line-height: 1.4;
  color: rgba(248, 250, 252, 0.9);
`;

const RelayNote = styled.p`
  margin: 0.85rem 0 0;
  font-family: "Inter", system-ui, sans-serif;
  font-size: clamp(0.72rem, 1.1vw, 0.8rem);
  line-height: 1.45;
  color: rgba(248, 250, 252, 0.55);
`;

const RelayKey = styled.strong`
  color: ${ORANGE};
  font-weight: 800;
  letter-spacing: 0.04em;
`;
